import { useCallback, useRef, useState, type ReactNode } from "react";
import { api, ApiError, fileToBase64 } from "../lib/api.js";
import type { ImportApplyResult, ImportPreview, ImportSceneChange, ProjectImportItem } from "../lib/types.js";
import { Modal } from "../components/Modal.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { diffLines } from "./diff.js";
import { formatNumber } from "../lib/format.js";

type Phase = "upload" | "preview" | "history";

export function ImportModal({
  projectId,
  onClose,
  onApplied
}: {
  projectId: string;
  onClose: () => void;
  onApplied?: () => void;
}) {
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>("upload");
  const [busy, setBusy] = useState(false);
  const [zipBase64, setZipBase64] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [accepted, setAccepted] = useState<Record<string, boolean>>({});
  const [openDiff, setOpenDiff] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [lastImportId, setLastImportId] = useState<string | null>(null);
  const [history, setHistory] = useState<ProjectImportItem[] | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const actionable = (preview?.scenes ?? []).filter((s) => s.status !== "unchanged");

  const loadFile = useCallback(
    async (file: File) => {
      if (!/\.zip$/i.test(file.name)) {
        toast.error("Please choose a ClovaScribe Book Project .zip file.");
        return;
      }
      setBusy(true);
      try {
        const b64 = await fileToBase64(file);
        const pv = await api.importPreview(projectId, b64);
        setZipBase64(b64);
        setFileName(file.name);
        setPreview(pv);
        // Default: accept every safe change (modified + renamed), not conflicts.
        const init: Record<string, boolean> = {};
        for (const s of pv.scenes) {
          if (s.status === "modified" || s.status === "renamed") init[s.sceneId] = true;
        }
        setAccepted(init);
        setResult(null);
        setPhase("preview");
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : "That file could not be read as a Book Project.");
      } finally {
        setBusy(false);
      }
    },
    [projectId, toast]
  );

  const acceptedIds = Object.entries(accepted)
    .filter(([, v]) => v)
    .map(([id]) => id);

  const apply = async () => {
    if (!zipBase64 || acceptedIds.length === 0) return;
    setBusy(true);
    try {
      const res = await api.importApply(projectId, zipBase64, acceptedIds);
      setResult(res);
      setLastImportId(res.importId);
      toast.success(`Imported ${res.applied} scene${res.applied === 1 ? "" : "s"}.`);
      onApplied?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "The import could not be applied.");
    } finally {
      setBusy(false);
    }
  };

  const undo = async (importId: string) => {
    setBusy(true);
    try {
      const res = await api.rollbackImport(projectId, importId);
      toast.success(`Rolled back — ${res.restored} scene${res.restored === 1 ? "" : "s"} restored.`);
      onApplied?.();
      if (history) setHistory((h) => (h ? h.map((it) => (it.id === importId ? { ...it, status: "rolled_back" } : it)) : h));
      if (importId === lastImportId) setResult(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Rollback failed.");
    } finally {
      setBusy(false);
    }
  };

  const openHistory = async () => {
    setBusy(true);
    try {
      const { imports } = await api.listImports(projectId);
      setHistory(imports);
      setPhase("history");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not load import history.");
    } finally {
      setBusy(false);
    }
  };

  // ---- Upload phase ----
  if (phase === "upload") {
    return (
      <Modal
        title="Import Book Project"
        subtitle="Load a ClovaScribe Book Project .zip edited outside the app"
        onClose={onClose}
        width={560}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={openHistory} disabled={busy}>
              <Icon name="history" size={14} /> Import history
            </button>
            <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          </>
        }
      >
        <div
          className="import-drop"
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files[0];
            if (f) void loadFile(f);
          }}
        >
          <Icon name={busy ? "clock" : "file"} size={26} className={busy ? "spin" : ""} style={{ color: "var(--accent)" }} />
          <div className="strong" style={{ marginTop: 8 }}>{busy ? "Reading…" : "Choose or drop a .zip"}</div>
          <div className="faint" style={{ fontSize: 12, marginTop: 4 }}>
            Only a Book Project exported from this book can be imported. Nothing is changed until you review and confirm.
          </div>
          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void loadFile(f);
              e.target.value = "";
            }}
          />
        </div>
      </Modal>
    );
  }

  // ---- History phase ----
  if (phase === "history") {
    return (
      <Modal
        title="Import history"
        subtitle="Roll back a previous Book Project import"
        onClose={onClose}
        width={560}
        footer={<button className="btn btn-ghost" onClick={() => setPhase("upload")} disabled={busy}>Back</button>}
      >
        {!history || history.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>No imports yet.</p>
        ) : (
          <ul className="import-history">
            {history.map((it) => (
              <li key={it.id} className="import-history-row">
                <div className="grow">
                  <div className="strong">{it.label}</div>
                  <div className="faint" style={{ fontSize: 11.5 }}>
                    {it.sceneCount} scene{it.sceneCount === 1 ? "" : "s"}
                    {it.status === "rolled_back" ? " · rolled back" : ""}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-ghost"
                  disabled={busy || it.status === "rolled_back"}
                  onClick={() => void undo(it.id)}
                >
                  <Icon name="history" size={14} /> {it.status === "rolled_back" ? "Undone" : "Undo"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    );
  }

  // ---- Preview phase ----
  const s = preview!.summary;
  return (
    <Modal
      title={result ? "Import complete" : "Review changes"}
      subtitle={result ? undefined : `${fileName} — exported ${new Date(preview!.manifest.exportedAt).toLocaleString()}`}
      onClose={onClose}
      width={760}
      footer={
        result ? (
          <>
            <button className="btn btn-ghost" onClick={() => void undo(result.importId)} disabled={busy}>
              <Icon name="history" size={14} /> Undo import
            </button>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => setPhase("upload")} disabled={busy}>Back</button>
            <button
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => {
                const next: Record<string, boolean> = {};
                for (const sc of preview!.scenes) if (sc.status === "modified" || sc.status === "renamed") next[sc.sceneId] = true;
                setAccepted(next);
              }}
            >
              Accept all safe
            </button>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setAccepted({})}>Reject all</button>
            <button className="btn btn-primary" onClick={() => void apply()} disabled={busy || acceptedIds.length === 0}>
              {busy ? "Applying…" : `Apply ${acceptedIds.length} scene${acceptedIds.length === 1 ? "" : "s"}`}
            </button>
          </>
        )
      }
    >
      {result ? (
        <div className="import-result">
          <Icon name="check" size={28} style={{ color: "var(--accent)" }} />
          <p className="strong" style={{ marginTop: 10 }}>
            Applied {result.applied} scene{result.applied === 1 ? "" : "s"}.
          </p>
          <p className="muted" style={{ fontSize: 12.5 }}>
            A snapshot named “{result.label}” was saved first. Use <strong>Undo import</strong> to restore the previous manuscript.
          </p>
        </div>
      ) : (
        <>
          <div className="import-summary">
            <Chip n={s.modified} label="Modified" tone="accent" />
            <Chip n={s.renamed} label="Renamed" tone="accent" />
            <Chip n={s.conflicts} label="Conflicts" tone="danger" />
            <Chip n={s.unchanged} label="Unchanged" />
            <Chip n={s.missing} label="Missing" />
            <Chip n={s.unknown} label="Unknown" tone={s.unknown ? "danger" : undefined} />
            <Chip n={s.invalid} label="Invalid files" tone={s.invalid ? "danger" : undefined} />
            <Chip n={s.referenceChanged} label="Reference changed" />
          </div>

          {actionable.length === 0 ? (
            <p className="muted" style={{ margin: "14px 0 0" }}>No scene prose or title changes were found in this file.</p>
          ) : (
            <div className="import-scenes">
              {actionable.map((sc) => (
                <SceneRow
                  key={sc.sceneId}
                  change={sc}
                  checked={!!accepted[sc.sceneId]}
                  onToggle={(v) => setAccepted((a) => ({ ...a, [sc.sceneId]: v }))}
                  diffOpen={!!openDiff[sc.sceneId]}
                  onToggleDiff={() => setOpenDiff((d) => ({ ...d, [sc.sceneId]: !d[sc.sceneId] }))}
                />
              ))}
            </div>
          )}

          {(preview!.unknownScenes.length > 0 || preview!.invalidFiles.length > 0 || preview!.referenceChanges.length > 0) && (
            <div className="import-notes">
              {preview!.referenceChanges.length > 0 && (
                <Note icon="info" title="Reference data changed (not applied)">
                  {preview!.referenceChanges.map((r) => r.file).join(", ")} — the Story Bible and other reference files are
                  context only in this version.
                </Note>
              )}
              {preview!.unknownScenes.length > 0 && (
                <Note icon="alert" title="Unknown scenes (ignored)">
                  {preview!.unknownScenes.length} file(s) reference a scene id that isn’t in this book. They were not matched
                  and nothing was changed.
                </Note>
              )}
              {preview!.invalidFiles.length > 0 && (
                <Note icon="alert" title="Invalid files (ignored)">
                  {preview!.invalidFiles.map((f) => `${f.file}: ${f.reason}`).join(" · ")}
                </Note>
              )}
              {preview!.missingScenes.length > 0 && (
                <Note icon="info" title="Scenes not in the import">
                  {preview!.missingScenes.length} live scene(s) had no file in this ZIP and were left untouched. Adding or
                  removing scenes isn’t supported in this version.
                </Note>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function SceneRow({
  change,
  checked,
  onToggle,
  diffOpen,
  onToggleDiff
}: {
  change: ImportSceneChange;
  checked: boolean;
  onToggle: (v: boolean) => void;
  diffOpen: boolean;
  onToggleDiff: () => void;
}) {
  const rows = diffOpen && change.currentBody !== undefined && change.importedBody !== undefined
    ? diffLines(change.currentBody, change.importedBody)
    : null;
  return (
    <div className={`import-scene ${change.conflict ? "is-conflict" : ""}`}>
      <div className="import-scene-head">
        <label className="import-check">
          <input type="checkbox" checked={checked} onChange={(e) => onToggle(e.target.checked)} />
        </label>
        <div className="grow" style={{ minWidth: 0 }}>
          <div className="row gap-2" style={{ flexWrap: "wrap" }}>
            <span className="strong truncate">{change.newTitle || change.oldTitle}</span>
            <span className="faint" style={{ fontSize: 11.5 }}>{change.chapterTitle}</span>
            <StatusBadge change={change} />
          </div>
          {change.titleChanged && (
            <div className="faint" style={{ fontSize: 11.5 }}>
              Title: “{change.oldTitle}” → “{change.newTitle}”
            </div>
          )}
          <div className="faint" style={{ fontSize: 11.5 }}>
            {formatNumber(change.currentWordCount)} → {formatNumber(change.importedWordCount)} words
            {change.proseChanged ? " · prose changed" : ""}
          </div>
        </div>
        {change.proseChanged && (
          <button className="btn btn-sm btn-ghost" onClick={onToggleDiff}>
            <Icon name={diffOpen ? "chevronDown" : "chevronRight"} size={14} /> Diff
          </button>
        )}
      </div>
      {rows && (
        <div className="import-diff">
          {rows.map((r, i) => (
            <div key={i} className={`diff-row diff-${r.type}`}>
              <span className="diff-gutter">{r.type === "del" ? "−" : r.type === "ins" ? "+" : " "}</span>
              <span className="diff-text">
                {r.parts
                  ? r.parts.map((p, j) => (p.type === "eq" ? <span key={j}>{p.text}</span> : <span key={j} className={`w-${p.type}`}>{p.text}</span>))
                  : r.text || " "}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ change }: { change: ImportSceneChange }) {
  if (change.conflict) return <span className="chip chip-danger">Conflict</span>;
  if (change.status === "modified") return <span className="chip chip-accent">Modified</span>;
  if (change.status === "renamed") return <span className="chip">Renamed</span>;
  return null;
}

function Chip({ n, label, tone }: { n: number; label: string; tone?: "accent" | "danger" }) {
  return (
    <span className={`import-stat ${tone ? `import-stat-${tone}` : ""} ${n === 0 ? "is-zero" : ""}`}>
      <span className="import-stat-n">{n}</span> {label}
    </span>
  );
}

function Note({ icon, title, children }: { icon: "info" | "alert"; title: string; children: ReactNode }) {
  return (
    <div className="import-note">
      <Icon name={icon === "alert" ? "alert" : "info"} size={15} style={{ color: icon === "alert" ? "var(--danger)" : "var(--text-faint)" }} />
      <div>
        <div className="strong" style={{ fontSize: 12.5 }}>{title}</div>
        <div className="faint" style={{ fontSize: 11.5 }}>{children}</div>
      </div>
    </div>
  );
}
